export const OVERHEAD_BUCKET_PAGE_SIZE = 8;
export const UNCATEGORIZED_BUCKET = "__uncategorized__";

export type OverheadBucketFilterNode = {
  id: string;
  name: string;
  bucket_key: string;
  category: string;
  children: OverheadBucketFilterNode[];
};

export function overheadBucketCategoryOptions(buckets: Array<{ category: string }>): string[] {
  const categories = new Set<string>();
  for (const bucket of buckets) {
    const category = bucket.category.trim();
    if (category) categories.add(category);
  }
  return [...categories].sort((a, b) => a.localeCompare(b));
}

export function bucketMatchesTaxonomyFilter(
  node: { name: string; bucket_key: string; category: string },
  query: string,
  category: string,
): boolean {
  const needle = query.trim().toLowerCase();
  const textHit =
    !needle ||
    node.name.toLowerCase().includes(needle) ||
    node.bucket_key.toLowerCase().includes(needle) ||
    node.category.toLowerCase().includes(needle);
  if (!textHit) return false;
  if (!category) return true;
  if (category === UNCATEGORIZED_BUCKET) return !node.category.trim();
  return node.category === category;
}

/** Keep a node when it matches, or when a descendant matches. Children are filtered the same way. */
export function filterOverheadBucketTree<T extends OverheadBucketFilterNode>(
  nodes: T[],
  query: string,
  category: string,
  alwaysIncludeId?: string | null,
): T[] {
  const out: T[] = [];
  for (const node of nodes) {
    const children = filterOverheadBucketTree(node.children as T[], query, category, alwaysIncludeId);
    const includeSelf = node.id === alwaysIncludeId || bucketMatchesTaxonomyFilter(node, query, category);
    if (includeSelf || children.length > 0) {
      out.push({ ...node, children });
    }
  }
  return out;
}

export type OverheadNestBucket = {
  id: string;
  parent_bucket_id: string | null;
  is_aggregate: boolean;
  has_children: boolean;
};

/** Where a dragged bucket should land. Null means the drop changes nothing. */
export function overheadNestParentId(
  source: OverheadNestBucket,
  target: OverheadNestBucket | null,
): { parent_bucket_id: string | null } | { error: string } | null {
  if (target && target.id === source.id) return { error: "A bucket cannot sit inside itself." };
  if (source.is_aggregate && target) return { error: "A folder stays at the top." };
  if (source.has_children && target) return { error: "Move the items under it out before nesting this bucket." };
  if (target?.parent_bucket_id) return { error: "Nest under a top-level bucket." };
  const nextParent = target ? target.id : null;
  if ((source.parent_bucket_id ?? null) === nextParent) return null;
  return { parent_bucket_id: nextParent };
}

export function pageOverheadBucketRoots<T>(items: T[], pageIndex: number, pageSize = OVERHEAD_BUCKET_PAGE_SIZE) {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safeIndex = Math.min(Math.max(pageIndex, 0), pageCount - 1);
  const start = safeIndex * pageSize;
  return {
    page: items.slice(start, start + pageSize),
    pageIndex: safeIndex,
    pageCount,
    total,
    rangeStart: total === 0 ? 0 : start + 1,
    rangeEnd: Math.min(start + pageSize, total),
  };
}
